(() => {
  const STORAGE_KEY = 'tajimarcheVendorCheckoutSession';
  const MESSAGE_KEY = 'tajimarcheVendorCheckoutMessage';
  const THANKS_KEY = 'tajimarcheVendorCheckoutThanks';
  const NOTE_STORAGE_KEY = 'tajimarcheVendorCheckoutNotes';
  const LOCKED_BODY_CLASS = 'tajimarche-vendor-checkout-active';

  const POLL_INTERVAL = 4000;
  const TIMEOUT_LIMIT = 5 * 60 * 1000;
  const INITIAL_GRACE = 12000;
  const REQUIRED_REMOVED_CONFIRMATIONS = 2;
  const MAX_NOTE_LENGTH = 1000;
  const MAX_STORAGE_VALUE_LENGTH = 300;
  const MAX_NOTE_ENTRIES = 30;
  const NOTE_TTL = 24 * 60 * 60 * 1000;

  let pollTimer = null;
  let pollInFlight = false;
  let checkoutPopup = null;
  let checkoutFinalizationInProgress = false;

  const route = (name, fallback) => {
    if (window.routes && window.routes[name]) return window.routes[name];
    return fallback;
  };

  const rootUrl = () => {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    const cartUrl = route('cart_url', '/cart');
    return cartUrl.replace(/cart\/?$/, '');
  };

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  const toSafeString = (value, maxLength = MAX_STORAGE_VALUE_LENGTH) => {
    const text = typeof value === 'string' ? value : value == null ? '' : String(value);
    return text.replace(/\u0000/g, '').slice(0, maxLength);
  };

  const normalizeNote = (note) => toSafeString(note, MAX_NOTE_LENGTH).replace(/^[\s\u3000]+/, '');

  const getPending = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const setPending = (session) => {
    window.isVendorCheckoutActive = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setCartInteractionLocked(true);
  };

  const clearPending = () => {
    window.isVendorCheckoutActive = false;
    localStorage.removeItem(STORAGE_KEY);
    setCartInteractionLocked(false);
  };

  const getCartData = () => {
    const dataElement = document.getElementById('TajimarcheVendorCartData');
    if (!dataElement) return { items: [], item_count: 0, note: '' };

    try {
      const parsed = JSON.parse(dataElement.textContent);
      return {
        ...parsed,
        items: Array.isArray(parsed.items) ? parsed.items : [],
        item_count: Number(parsed.item_count || 0),
        note: toSafeString(parsed.note || '', MAX_NOTE_LENGTH),
      };
    } catch (error) {
      return { items: [], item_count: 0, note: '' };
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${route('cart_url', '/cart')}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('カート情報の取得に失敗しました。');
    return response.json();
  };

  const updateCart = async (payload, errorMessage = 'カート情報の更新に失敗しました。') => {
    const response = await fetch(route('cart_update_url', '/cart/update.js'), {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(errorMessage);
    return response.json();
  };

  const updateCartNote = async (note) => updateCart(
    { note: normalizeNote(note) },
    'カート備考欄の更新に失敗しました。'
  );

  const updateCartForVendorCheckout = async (items, note) => {
    const updates = items.reduce((result, item) => {
      if (item.key) result[item.key] = 0;
      return result;
    }, {});

    const payload = { note: normalizeNote(note) };
    if (Object.keys(updates).length > 0) payload.updates = updates;

    return updateCart(payload, 'カート商品の一時調整に失敗しました。');
  };

  const clearCartNoteQuietly = async () => {
    try {
      await updateCartNote('');
    } catch (error) {
      console.warn('Failed to clear cart note.', error);
    }
  };

  const restoreItems = async (items) => {
    if (!items.length) return fetchCart();
    const restorePayload = items.map((item) => {
      const payload = { id: item.variant_id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length > 0) payload.properties = item.properties;
      if (item.selling_plan) payload.selling_plan = item.selling_plan;
      return payload;
    });

    const response = await fetch(route('cart_add_url', '/cart/add.js'), {
      method: 'POST',
      headers: jsonHeaders,
      credentials: 'same-origin',
      body: JSON.stringify({ items: restorePayload }),
    });
    if (!response.ok) throw new Error('退避していた商品の復元に失敗しました。');
    return response.json();
  };

  const restoreMissingItems = async (items) => {
    if (!items.length) return fetchCart();
    const cart = await fetchCart();
    const existingKeys = new Set(cart.items.map((item) => item.key));
    const missingItems = items.filter((item) => item.key && !existingKeys.has(item.key));
    if (!missingItems.length) return cart;
    await restoreItems(missingItems);
    return fetchCart();
  };

  const normalizeNoteStore = (store) => {
    const rawNotes = Array.isArray(store && store.notes) ? store.notes : Array.isArray(store) ? store : [];
    const notes = rawNotes
      .map((entry) => {
        const vendorKey = toSafeString(entry && entry.vendorKey);
        const note = normalizeNote(entry && entry.note ? entry.note : '');
        if (!vendorKey || !note) return null;

        const updatedAt = Number(entry.updatedAt);
        return {
          vendorKey,
          note,
          itemKeys: Array.isArray(entry.itemKeys)
            ? entry.itemKeys.map((key) => toSafeString(key)).filter(Boolean).slice(0, 50)
            : [],
          variantIds: Array.isArray(entry.variantIds)
            ? entry.variantIds.map((id) => toSafeString(id)).filter(Boolean).slice(0, 50)
            : [],
          updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.updatedAt - b.updatedAt)
      .slice(-MAX_NOTE_ENTRIES);

    return { version: 1, notes };
  };

  const readNoteStore = () => {
    try {
      const raw = localStorage.getItem(NOTE_STORAGE_KEY);
      if (!raw) return { version: 1, notes: [] };
      return normalizeNoteStore(JSON.parse(raw));
    } catch (error) {
      localStorage.removeItem(NOTE_STORAGE_KEY);
      return { version: 1, notes: [] };
    }
  };

  const writeNoteStore = (store) => {
    const normalized = normalizeNoteStore(store);
    if (!normalized.notes.length) {
      localStorage.removeItem(NOTE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(NOTE_STORAGE_KEY, JSON.stringify(normalized));
  };

  const getVendorItemsFromData = (vendorKey, data = getCartData()) => {
    const safeVendorKey = toSafeString(vendorKey);
    return (data.items || []).filter((item) => toSafeString(item.vendor_key) === safeVendorKey);
  };

  const getItemKeys = (items) => items.map((item) => toSafeString(item.key)).filter(Boolean);

  const getVariantIds = (items) => items.map((item) => toSafeString(item.variant_id)).filter(Boolean);

  const findVendorNoteEntry = (vendorKey) => {
    const safeVendorKey = toSafeString(vendorKey);
    return readNoteStore().notes.find((entry) => entry.vendorKey === safeVendorKey) || null;
  };

  const saveVendorNote = (vendorKey, note) => {
    const safeVendorKey = toSafeString(vendorKey);
    if (!safeVendorKey) return;

    const normalizedNote = normalizeNote(note);
    const store = readNoteStore();
    const notes = store.notes.filter((entry) => entry.vendorKey !== safeVendorKey);

    if (!normalizedNote) {
      writeNoteStore({ ...store, notes });
      return;
    }

    const vendorItems = getVendorItemsFromData(safeVendorKey);
    notes.push({
      vendorKey: safeVendorKey,
      note: normalizedNote,
      itemKeys: getItemKeys(vendorItems),
      variantIds: getVariantIds(vendorItems),
      updatedAt: Date.now(),
    });
    writeNoteStore({ ...store, notes });
  };

  const clearVendorNote = (vendorKey) => {
    const safeVendorKey = toSafeString(vendorKey);
    if (!safeVendorKey) return;
    const store = readNoteStore();
    writeNoteStore({ ...store, notes: store.notes.filter((entry) => entry.vendorKey !== safeVendorKey) });
  };

  const clearAllVendorNotes = () => {
    localStorage.removeItem(NOTE_STORAGE_KEY);
  };

  const getPendingVendorKeys = (session) => {
    const keys = new Set();
    if (!session) return keys;
    if (session.vendorKey) keys.add(toSafeString(session.vendorKey));
    [...(session.selectedItems || []), ...(session.removedItems || [])].forEach((item) => {
      if (item.vendor_key) keys.add(toSafeString(item.vendor_key));
    });
    return keys;
  };

  const cleanupVendorNotes = () => {
    const data = getCartData();
    const currentItems = data.items || [];
    const pending = getPending();
    const pendingVendorKeys = getPendingVendorKeys(pending);

    if (!currentItems.length && !pending) {
      clearAllVendorNotes();
      return;
    }

    const currentByVendor = new Map();
    currentItems.forEach((item) => {
      const vendorKey = toSafeString(item.vendor_key);
      if (!vendorKey) return;
      if (!currentByVendor.has(vendorKey)) {
        currentByVendor.set(vendorKey, { itemKeys: new Set(), variantIds: new Set() });
      }
      const current = currentByVendor.get(vendorKey);
      const itemKey = toSafeString(item.key);
      const variantId = toSafeString(item.variant_id);
      if (itemKey) current.itemKeys.add(itemKey);
      if (variantId) current.variantIds.add(variantId);
    });

    const now = Date.now();
    const store = readNoteStore();
    const notes = store.notes.filter((entry) => {
      if (now - entry.updatedAt > NOTE_TTL) return false;
      if (pendingVendorKeys.has(entry.vendorKey)) return true;

      const current = currentByVendor.get(entry.vendorKey);
      if (!current) return false;
      if (entry.itemKeys.length) return entry.itemKeys.some((key) => current.itemKeys.has(key));
      if (entry.variantIds.length) return entry.variantIds.some((id) => current.variantIds.has(id));
      return false;
    });

    writeNoteStore({ ...store, notes });
  };

  const findVendorNoteInput = (vendorKey) => {
    const safeVendorKey = toSafeString(vendorKey);
    return Array.from(document.querySelectorAll('[data-tajimarche-vendor-note]')).find((input) => {
      return toSafeString(input.dataset.vendorKey) === safeVendorKey;
    }) || null;
  };

  const restoreVendorNoteInputs = () => {
    const store = readNoteStore();
    document.querySelectorAll('[data-tajimarche-vendor-note]').forEach((input) => {
      const vendorKey = toSafeString(input.dataset.vendorKey);
      const entry = store.notes.find((noteEntry) => noteEntry.vendorKey === vendorKey);
      const note = entry ? entry.note : '';
      if (input.value !== note) input.value = note;
    });
  };

  const getVendorNoteForCheckout = (vendorKey) => {
    const input = findVendorNoteInput(vendorKey);
    if (input) {
      const note = normalizeNote(input.value);
      if (input.value !== note) input.value = note;
      saveVendorNote(vendorKey, note);
      return note;
    }
    const entry = findVendorNoteEntry(vendorKey);
    return entry ? entry.note : '';
  };

  const selectedItemsStillInCart = (cart, session) => {
    const selectedKeys = new Set(session.selectedKeys || []);
    if (selectedKeys.size > 0) return cart.items.some((item) => selectedKeys.has(item.key));

    const selectedVariantIds = new Set((session.selectedItems || []).map((item) => String(item.variant_id)));
    if (!selectedVariantIds.size) return false;
    return cart.items.some((item) => selectedVariantIds.has(String(item.variant_id)));
  };

  const setMessage = (message) => {
    const element = document.querySelector('[data-tajimarche-vendor-cart-message]');
    if (!element) return;
    if (!message) {
      element.hidden = true;
      element.textContent = '';
      return;
    }
    element.hidden = false;
    element.textContent = message;
  };

  const CART_LOCK_SELECTOR = [
    '[data-tajimarche-vendor-checkout]',
    '[data-tajimarche-vendor-note]',
    '[data-tajimarche-vendor-cart] .quantity__button',
    '[data-tajimarche-vendor-cart] .quantity__input',
    '[data-tajimarche-vendor-cart] cart-remove-button',
    '[data-tajimarche-vendor-cart] cart-remove-button a',
    'cart-drawer .quantity__button',
    'cart-drawer .quantity__input',
    'cart-drawer cart-remove-button',
    'cart-drawer cart-remove-button a',
    'button[name="checkout"]',
    'input[name="checkout"]',
  ].join(',');

  const isCartInteractionLocked = () => {
    if (window.isVendorCheckoutActive === true) return true;
    try {
      return Boolean(localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return false;
    }
  };

  const getCartMutationControls = () => Array.from(document.querySelectorAll(CART_LOCK_SELECTOR)).filter((element) => {
    return !element.closest('[data-tajimarche-vendor-restore]');
  });

  const setControlLocked = (element, locked) => {
    if (!element) return;

    if (locked && !element.dataset.tajimarcheLockManaged) {
      element.dataset.tajimarcheLockManaged = 'true';
      element.dataset.tajimarcheWasDisabled = element.disabled ? 'true' : 'false';
      element.dataset.tajimarcheWasReadonly = element.readOnly ? 'true' : 'false';
      if (element.hasAttribute('tabindex')) {
        element.dataset.tajimarchePreviousTabindex = element.getAttribute('tabindex');
      }
    }

    if (element.matches('button, input, select')) {
      if (locked) {
        element.disabled = true;
      } else if (element.dataset.tajimarcheLockManaged === 'true' && element.dataset.tajimarcheWasDisabled !== 'true') {
        element.disabled = false;
      }
    }

    if (element.matches('textarea')) {
      if (locked) {
        element.readOnly = true;
        element.setAttribute('aria-disabled', 'true');
      } else if (element.dataset.tajimarcheLockManaged === 'true') {
        if (element.dataset.tajimarcheWasReadonly !== 'true') element.readOnly = false;
        element.removeAttribute('aria-disabled');
      }
    }

    if (element.matches('a, cart-remove-button')) {
      if (locked) {
        element.setAttribute('aria-disabled', 'true');
        element.setAttribute('tabindex', '-1');
      } else if (element.dataset.tajimarcheLockManaged === 'true') {
        element.removeAttribute('aria-disabled');
        if (element.dataset.tajimarchePreviousTabindex !== undefined) {
          element.setAttribute('tabindex', element.dataset.tajimarchePreviousTabindex);
        } else {
          element.removeAttribute('tabindex');
        }
      }
    }

    element.classList.toggle('tajimarche-is-locked', locked);

    if (!locked && element.dataset.tajimarcheLockManaged === 'true') {
      delete element.dataset.tajimarcheLockManaged;
      delete element.dataset.tajimarcheWasDisabled;
      delete element.dataset.tajimarcheWasReadonly;
      delete element.dataset.tajimarchePreviousTabindex;
    }
  };

  const setCartInteractionLocked = (locked) => {
    const shouldLock = Boolean(locked);
    const root = document.body || document.documentElement;
    root.classList.toggle(LOCKED_BODY_CLASS, shouldLock);
    root.setAttribute('data-tajimarche-vendor-checkout-active', shouldLock ? 'true' : 'false');

    const cartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (cartItems) cartItems.classList.toggle('tajimarche-cart-locked', shouldLock);

    getCartMutationControls().forEach((element) => setControlLocked(element, shouldLock));
  };

  const refreshCartInteractionLock = () => {
    setCartInteractionLocked(isCartInteractionLocked());
  };

  const isLockedMutationTarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    const control = target.closest(CART_LOCK_SELECTOR);
    if (!control) return false;
    if (control.closest('[data-tajimarche-vendor-restore]')) return false;
    return true;
  };

  const blockLockedCartMutation = (event) => {
    if (!isCartInteractionLocked()) return;
    if (!isLockedMutationTarget(event.target)) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    refreshCartInteractionLock();
  };

  const setCheckoutButtonsDisabled = (disabled) => {
    document.querySelectorAll('[data-tajimarche-vendor-checkout]').forEach((btn) => {
      btn.disabled = disabled;
      if (disabled) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    });
    setCartInteractionLocked(disabled || isCartInteractionLocked());
  };

  const showStoredMessage = () => {
    const message = sessionStorage.getItem(MESSAGE_KEY);
    if (message) {
      setMessage(message);
      sessionStorage.removeItem(MESSAGE_KEY);
    }
  };

  const showThanksIfNeeded = () => {
    if (sessionStorage.getItem(THANKS_KEY) !== '1') return;
    const defaultText = document.querySelector('.tajimarche-empty-default');
    const thanksText = document.querySelector('.tajimarche-empty-thanks');
    if (defaultText && thanksText) {
      defaultText.hidden = true;
      thanksText.hidden = false;
    }
    sessionStorage.removeItem(THANKS_KEY);
  };

  const checkoutUrl = () => `${rootUrl()}checkout`;

  const isMobileDevice = () => window.matchMedia('(max-width: 749px)').matches || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

  const showCheckoutStatus = (session, message, title) => {
    const status = document.querySelector('[data-tajimarche-vendor-checkout-status]');
    const statusTitle = document.querySelector('[data-tajimarche-vendor-checkout-status-title]');
    const statusText = document.querySelector('[data-tajimarche-vendor-checkout-status-text]');
    if (!status) return;

    status.hidden = false;
    setCheckoutButtonsDisabled(true);
    refreshCartInteractionLock();
    if (statusTitle) statusTitle.textContent = title || '別ウィンドウで決済中です';
    if (statusText) statusText.textContent = message || '決済が完了すると、このカート画面は自動で更新されます。';
  };

  const reloadCart = () => window.location.href = route('cart_url', '/cart');

  const closeCheckoutPopup = () => {
    if (checkoutPopup && !checkoutPopup.closed) {
      try { checkoutPopup.close(); } catch (error) {}
    }
    checkoutPopup = null;
  };

  const stopPolling = () => {
    if (pollTimer) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
    pollInFlight = false;
  };

  const completeVendorCheckout = async ({ closePopup = true } = {}) => {
    if (checkoutFinalizationInProgress) return;
    checkoutFinalizationInProgress = true;

    const session = getPending();
    if (!session) {
      window.isVendorCheckoutActive = false;
      refreshCartInteractionLock();
      checkoutFinalizationInProgress = false;
      return;
    }

    stopPolling();
    setMessage('決済完了を確認しました。カートを更新しています...');

    try {
      await restoreMissingItems(session.removedItems || []);
      await updateCartNote('');

      const cart = await fetchCart();
      const selectedStillExists = selectedItemsStillInCart(cart, session);
      if (!selectedStillExists) clearVendorNote(session.vendorKey);

      clearPending();

      if (cart.item_count === 0) {
        clearAllVendorNotes();
        sessionStorage.setItem(THANKS_KEY, '1');
      } else {
        cleanupVendorNotes();
        sessionStorage.setItem(MESSAGE_KEY, '決済が完了しました。未決済の商品が残っています。続けてお手続きください。');
      }

      if (closePopup && session.deviceMode !== 'mobile') closeCheckoutPopup();
      reloadCart();
    } catch (error) {
      checkoutFinalizationInProgress = false;
      setMessage('カートの更新に失敗しました。少し時間をおいて再度お試しください。');
    }
  };

  const restoreCancelledCheckout = async () => {
    if (checkoutFinalizationInProgress) return;
    checkoutFinalizationInProgress = true;

    const session = getPending();
    if (!session) {
      window.isVendorCheckoutActive = false;
      refreshCartInteractionLock();
      checkoutFinalizationInProgress = false;
      return;
    }

    stopPolling();
    setMessage('決済状況を確認しています...');

    try {
      const currentCart = await fetchCart();
      const selectedStillExists = selectedItemsStillInCart(currentCart, session);

      if (!selectedStillExists) {
        setMessage('決済完了を確認しました。カートを更新しています...');
        await restoreMissingItems(session.removedItems || []);
        await updateCartNote('');
        clearVendorNote(session.vendorKey);
        clearPending();

        const updatedCart = await fetchCart();
        if (updatedCart.item_count === 0) {
          clearAllVendorNotes();
          sessionStorage.setItem(THANKS_KEY, '1');
        } else {
          cleanupVendorNotes();
          sessionStorage.setItem(MESSAGE_KEY, '決済が完了しました。未決済の商品が残っています。続けてお手続きください。');
        }

        if (session.deviceMode !== 'mobile') closeCheckoutPopup();
        reloadCart();
        return;
      }

      setMessage('カートを再読み込みしています...');
      await restoreMissingItems(session.removedItems || []);
      await updateCartNote('');
      clearPending();
      cleanupVendorNotes();
      closeCheckoutPopup();
      sessionStorage.setItem(MESSAGE_KEY, 'カートを再読み込みしました。必要に応じて再度お手続きください。');
      reloadCart();
    } catch (error) {
      checkoutFinalizationInProgress = false;
      setMessage('カートの復元に失敗しました。少し時間をおいて再度お試しください。');
    }
  };

  const checkCompletion = async () => {
    const session = getPending();
    if (!session) {
      stopPolling();
      return;
    }

    if (pollInFlight) return;
    pollInFlight = true;

    try {
      const monitoringStartedAt = Number(session.monitoringStartedAt || session.startedAt || Date.now());
      const elapsed = Date.now() - monitoringStartedAt;

      if (elapsed < INITIAL_GRACE) {
        setPending({ ...session, removedConfirmationCount: 0, lastPolledAt: Date.now() });
        return;
      }

      const cart = await fetchCart();
      const selectedStillInCart = selectedItemsStillInCart(cart, session);
      const isRemoved = cart.item_count === 0 || !selectedStillInCart;

      if (isRemoved) {
        const removedConfirmationCount = Number(session.removedConfirmationCount || 0) + 1;
        if (removedConfirmationCount >= REQUIRED_REMOVED_CONFIRMATIONS) {
          await completeVendorCheckout({ closePopup: true });
          return;
        }
        setPending({ ...session, removedConfirmationCount, lastPolledAt: Date.now() });
        return;
      }

      if (elapsed > TIMEOUT_LIMIT) {
        await restoreCancelledCheckout();
        return;
      }

      setPending({ ...session, removedConfirmationCount: 0, lastPolledAt: Date.now() });
    } catch (error) {
      console.warn('Vendor checkout polling failed.', error);
    } finally {
      pollInFlight = false;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollTimer = window.setInterval(checkCompletion, POLL_INTERVAL);
  };

  const openCheckoutWindow = () => {
    const mobile = isMobileDevice();
    try {
      let checkoutWindow = null;
      if (mobile) {
        checkoutWindow = window.open('', '_blank');
      } else {
        const width = 550;
        const height = 750;
        const left = Math.max(0, Math.round((window.screen.width - width) / 2));
        const top = Math.max(0, Math.round((window.screen.height - height) / 2));
        checkoutWindow = window.open('', 'shopify_checkout', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
      }
      if (checkoutWindow) {
        checkoutWindow.document.write('<p style="font-family: sans-serif; padding: 24px; line-height: 1.7;">決済画面を準備しています...</p>');
      }
      return { windowRef: checkoutWindow, deviceMode: mobile ? 'mobile' : 'desktop' };
    } catch (error) {
      return { windowRef: null, deviceMode: mobile ? 'mobile' : 'desktop' };
    }
  };

  const startVendorCheckout = async (button) => {
    window.isVendorCheckoutActive = true;
    cleanupVendorNotes();

    const vendorKey = button.dataset.vendorKey;
    const vendorName = button.dataset.vendorName || '選択したメーカー';
    const data = getCartData();
    const allItems = data.items || [];
    const selectedItems = allItems.filter((item) => item.vendor_key === vendorKey);
    const removedItems = allItems.filter((item) => item.vendor_key !== vendorKey);
    const vendorNote = getVendorNoteForCheckout(vendorKey);
    const originalButtonText = button.textContent;

    if (!selectedItems.length) {
      window.isVendorCheckoutActive = false;
      setMessage('対象の商品が見つかりませんでした。ページを再読み込みしてからお試しください。');
      return;
    }

    if (getPending()) {
      setMessage('別ウィンドウでの決済状況を確認しています。');
      setCheckoutButtonsDisabled(true);
      return;
    }

    const checkoutWindowData = openCheckoutWindow();
    const checkoutWindow = checkoutWindowData.windowRef;

    if (!checkoutWindow) {
      window.isVendorCheckoutActive = false;
      setMessage('決済画面を開けませんでした。ポップアップブロックを解除するか、もう一度お試しください。');
      return;
    }

    checkoutPopup = checkoutWindowData.deviceMode === 'desktop' ? checkoutWindow : null;

    setCheckoutButtonsDisabled(true);
    button.disabled = true;
    button.textContent = '決済画面を準備しています...';
    setMessage('決済画面を準備しています...');

    const session = {
      id: `${Date.now()}`,
      vendorKey,
      vendorName,
      selectedKeys: selectedItems.map((item) => item.key),
      selectedItems,
      removedItems,
      createdAt: new Date().toISOString(),
      startedAt: Date.now(),
      monitoringStartedAt: Date.now(),
      removedConfirmationCount: 0,
      deviceMode: checkoutWindowData.deviceMode,
    };

    setPending(session);

    try {
      await updateCartForVendorCheckout(removedItems, vendorNote);
      showCheckoutStatus(session, '決済が完了すると、このカート画面は自動で更新されます。', '別ウィンドウで決済中です');

      if (checkoutWindow && !checkoutWindow.closed) {
        checkoutWindow.location.href = checkoutUrl();
        startPolling();
      } else {
        window.location.href = checkoutUrl();
      }
    } catch (error) {
      let rollbackSucceeded = false;
      try {
        await restoreMissingItems(removedItems);
        await clearCartNoteQuietly();
        rollbackSucceeded = true;
      } catch (rollbackError) {
        console.warn('Vendor checkout rollback failed.', rollbackError);
      }

      if (rollbackSucceeded) clearPending();
      closeCheckoutPopup();
      setCheckoutButtonsDisabled(false);
      button.textContent = originalButtonText;
      setMessage('決済の準備に失敗しました。ページを再読み込みしてからお試しください。');
    }
  };

  const handleDocumentClick = (event) => {
    const checkoutButton = event.target.closest('[data-tajimarche-vendor-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      startVendorCheckout(checkoutButton);
      return;
    }

    const restoreButton = event.target.closest('[data-tajimarche-vendor-restore]');
    if (restoreButton) {
      event.preventDefault();
      restoreCancelledCheckout();
      return;
    }

  };

  const handleDocumentInput = (event) => {
    const noteInput = event.target.closest('[data-tajimarche-vendor-note]');
    if (!noteInput) return;

    const note = normalizeNote(noteInput.value);
    if (noteInput.value !== note) noteInput.value = note;
    saveVendorNote(noteInput.dataset.vendorKey, note);
  };

  const handleDocumentChange = (event) => {
    const noteInput = event.target.closest('[data-tajimarche-vendor-note]');
    if (!noteInput) return;
    event.stopPropagation();

    const note = normalizeNote(noteInput.value);
    if (noteInput.value !== note) noteInput.value = note;
    saveVendorNote(noteInput.dataset.vendorKey, note);
  };

  const clearGlobalCartNoteIfNeeded = () => {
    const data = getCartData();
    if (getPending()) return;
    if (data.note) clearCartNoteQuietly();
  };

  const initVendorNotes = () => {
    cleanupVendorNotes();
    restoreVendorNoteInputs();
    clearGlobalCartNoteIfNeeded();
  };

  const initPendingSession = () => {
    const session = getPending();
    if (!session) {
      window.isVendorCheckoutActive = false;
      refreshCartInteractionLock();
      return;
    }

    window.isVendorCheckoutActive = true;
    setMessage('決済の完了を確認しています。');
    showCheckoutStatus(session, '決済が完了すると、このカート画面は自動で更新されます。', '決済の完了を確認しています');
    refreshCartInteractionLock();
    startPolling();
    checkCompletion();
  };

  const subscribeCartUpdates = () => {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          window.setTimeout(() => {
            cleanupVendorNotes();
            restoreVendorNoteInputs();
            refreshCartInteractionLock();
          }, 0);
        });
      }
    } catch (error) {}
  };

  document.addEventListener('click', blockLockedCartMutation, true);
  document.addEventListener('input', blockLockedCartMutation, true);
  document.addEventListener('change', blockLockedCartMutation, true);
  document.addEventListener('keydown', blockLockedCartMutation, true);
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('input', handleDocumentInput);
  document.addEventListener('change', handleDocumentChange, true);
  document.addEventListener('DOMContentLoaded', () => {
    showStoredMessage();
    showThanksIfNeeded();
    initVendorNotes();
    initPendingSession();
    refreshCartInteractionLock();
    subscribeCartUpdates();
  });
  window.addEventListener('focus', () => {
    refreshCartInteractionLock();
    checkCompletion();
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      refreshCartInteractionLock();
      checkCompletion();
    }
  });
})();
